import React, { useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "axios";
import * as moment from "moment";
import {
  getDatePickerFormat,
  getDateFormat,
  getCalendarLocale,
} from "@utils/datePickerUtils";
import DatePicker from "react-multi-date-picker";
import ClipLoader from "react-spinners/ClipLoader";
import { Dialog, Button, Dropdown, CustomCheckBox, Image } from "hesus-ui-kit";
import { handleApiError } from "@utils/errorHandler";
import UserInfo from "@utils/UserInfo";
import { useGetGeneralParameters } from "@hooks/useGeneralParams";
import { RequiredMessage } from "@utils/extra-style";
import closeIcon from "@icons/ic-close-white.svg";
import {
  HeaderWrapper,
  Title,
  FormBody,
  ExplanationBox,
  ExplanationText,
  GridRow,
  FieldLabel,
  RequiredStar,
  DatePickerWrapper,
  CheckboxRow,
  SitesSection,
  SitesHeader,
  SitesList,
  SiteCard,
  SiteTitle,
  SiteClient,
  GenerationStartedMessage,
  LoaderContainer,
  ButtonWrapper,
} from "./BulkGeneration.styled";

const UK_COUNTRY_ID = 1;

const getDefaultPeriod = (countryId) => {
  if (Number(countryId) === UK_COUNTRY_ID) {
    return {
      startDate: moment().subtract(1, "weeks").startOf("isoWeek").toDate(),
      endDate: moment().subtract(1, "weeks").endOf("isoWeek").toDate(),
    };
  }
  return {
    startDate: moment().subtract(1, "months").startOf("month").toDate(),
    endDate: moment().subtract(1, "months").endOf("month").toDate(),
  };
};

const getSiteId = (site) => site.siteId;
const getSiteName = (site) => site.siteName ?? "";
const getSiteClient = (site) => site.clientName ?? "";

/**
 * Bulk invoices creation popup
 * Opened from the invoice list "Bulk generation" action.
 * @component
 */
const BulkGeneration = ({ t, onClose }) => {
  const userDetails = UserInfo.getUser();
  const userMainCountry = UserInfo.countryInfo();
  const defaultCountryId = userMainCountry?.country?.id ?? "";
  const isCountryVisible =
    !!userDetails?.techAdmin || (userDetails?.countries?.length ?? 0) > 1;

  const defaultPeriod = getDefaultPeriod(defaultCountryId);
  const generalParams = useGetGeneralParameters();
  const countryOptions = [...(generalParams?.countryList ?? [])]
    .sort((a, b) => (a.label ?? "").localeCompare(b.label ?? ""))
    .filter((country) => country.platform === 1)
    .map((country) => ({ label: t(country.label), value: country.value }));

  const [isValidated, setIsValidated] = useState(false);
  const [isLoadingSites, setIsLoadingSites] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGenerationStarted, setIsGenerationStarted] = useState(false);
  const [sites, setSites] = useState([]);
  const [formData, setFormData] = useState({
    country: { required: true, value: defaultCountryId, error: "" },
    startDate: { required: true, value: defaultPeriod.startDate, error: "" },
    endDate: { required: true, value: defaultPeriod.endDate, error: "" },
    includeNotReady: { required: false, value: false, error: "" },
  });

  const onFormChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: { ...prev[key], value, error: "" },
    }));
  };

  const onCountryChange = ({ value }) => {
    const period = getDefaultPeriod(value);
    setFormData((prev) => ({
      ...prev,
      country: { ...prev.country, value, error: "" },
      startDate: { ...prev.startDate, value: period.startDate, error: "" },
      endDate: { ...prev.endDate, value: period.endDate, error: "" },
    }));
  };

  const validateForm = () => {
    let isFormValid = true;
    const updatedFields = {};
    ["country", "startDate", "endDate"].forEach((key) => {
      if (formData[key].required && !formData[key].value) {
        updatedFields[key] = {
          ...formData[key],
          error: "GENERAL_MANDATORY_ERROR",
        };
        isFormValid = false;
      }
    });
    if (!isFormValid) {
      setFormData((prev) => ({ ...prev, ...updatedFields }));
    }
    return isFormValid;
  };

  const fetchSites = () => {
    setIsLoadingSites(true);
    axios
      .get("/clientInvoices/bulkGeneration", {
        params: {
          startDate: moment(formData.startDate.value).format("YYYY-MM-DD"),
          endDate: moment(formData.endDate.value).format("YYYY-MM-DD"),
          country: formData.country.value,
          readyInvoicingOnly: formData.includeNotReady.value ? 0 : 1,
        },
      })
      .then((response) => {
        setSites(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        setSites([]);
        handleApiError(error);
      })
      .finally(() => setIsLoadingSites(false));
  };

  const onValidate = () => {
    if (!validateForm()) {
      return;
    }
    setIsValidated(true);
    fetchSites();
  };

  const onGenerate = () => {
    setIsGenerating(true);
    axios
      .post("/clientInvoices/bulkGeneration", {
        adminId: userDetails?.id,
        countryId: formData.country.value,
        startDate: moment(formData.startDate.value).format("YYYY-MM-DD"),
        endDate: moment(formData.endDate.value).format("YYYY-MM-DD"),
        readyInvoicingOnly: formData.includeNotReady.value ? 0 : 1,
        sites: sites.map((site) => getSiteId(site)),
      })
      .then(() => setIsGenerationStarted(true))
      .catch((error) => handleApiError(error))
      .finally(() => setIsGenerating(false));
  };

  return (
    <Dialog width="50rem">
      <div className="bulk-generation">
        <HeaderWrapper>
          <Title>{t("INVOICES_BULK_GENERATION_TITLE")}</Title>
          <Button
            onClick={onClose}
            styles={{
              backgroundColor: "transparent",
              border: "none",
              height: "auto",
              padding: 0,
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <Image
              src={closeIcon}
              alt={t("CLOSE")}
              styles={{ width: "auto" }}
            />
          </Button>
        </HeaderWrapper>
        <FormBody>
          {isGenerationStarted && (
            <GenerationStartedMessage>
              {t("INVOICES_BULK_GENERATION_STARTED_MESSAGE")}
            </GenerationStartedMessage>
          )}
          {!isGenerationStarted && (
            <>
              <ExplanationBox>
                <ExplanationText>
                  {t("INVOICES_BULK_GENERATION_EXPLANATIONS")}
                </ExplanationText>
              </ExplanationBox>
              <GridRow
                column={isCountryVisible ? "1.4fr 1fr 1fr" : "1fr 1fr"}
                marginTop="15px"
              >
                {isCountryVisible && (
                  <div>
                    <Dropdown
                      name="country"
                      label={t("COUNTRY")}
                      required
                      value={formData.country.value}
                      options={countryOptions}
                      defaultOption={t("COUNTRY")}
                      onChange={onCountryChange}
                      disabled={isValidated}
                      labelStyles={{
                        fontWeight: "bold",
                        color:
                          formData.country.error !== "" ? "red" : "#1bace4",
                      }}
                      error={
                        formData.country.error !== ""
                          ? t(formData.country.error)
                          : ""
                      }
                    />
                  </div>
                )}
                <div>
                  <FieldLabel htmlFor="bulk-generation-start-date">
                    {t("INVOICES_BULK_GENERATION_START")}
                    <RequiredStar>*</RequiredStar>
                  </FieldLabel>
                  <DatePickerWrapper
                    disabled={isValidated}
                    hasError={formData.startDate.error !== ""}
                  >
                    <DatePicker
                      inputClass="bulk-generation__date-input"
                      containerClassName="bulk-generation__datepicker-container"
                      calendarPosition="bottom-center"
                      onChange={(e) =>
                        onFormChange(
                          "startDate",
                          e ? moment(e.toDate()).format("YYYY-MM-DD") : ""
                        )
                      }
                      format={getDatePickerFormat()}
                      locale={getCalendarLocale()}
                      value={
                        getDateFormat(formData.startDate.value, "YYYY-MM-DD") ||
                        ""
                      }
                      disabled={isValidated}
                      name="bulk-generation-start-date"
                    />
                  </DatePickerWrapper>
                  {formData.startDate.error !== "" && (
                    <RequiredMessage>
                      {t(formData.startDate.error)}
                    </RequiredMessage>
                  )}
                </div>
                <div>
                  <FieldLabel htmlFor="bulk-generation-end-date">
                    {t("INVOICES_BULK_GENERATION_END")}
                    <RequiredStar>*</RequiredStar>
                  </FieldLabel>
                  <DatePickerWrapper
                    disabled={isValidated}
                    hasError={formData.endDate.error !== ""}
                  >
                    <DatePicker
                      inputClass="bulk-generation__date-input"
                      containerClassName="bulk-generation__datepicker-container"
                      calendarPosition="bottom-center"
                      onChange={(e) =>
                        onFormChange(
                          "endDate",
                          e ? moment(e.toDate()).format("YYYY-MM-DD") : ""
                        )
                      }
                      format={getDatePickerFormat()}
                      locale={getCalendarLocale()}
                      value={
                        getDateFormat(formData.endDate.value, "YYYY-MM-DD") ||
                        ""
                      }
                      disabled={isValidated}
                      name="bulk-generation-end-date"
                    />
                  </DatePickerWrapper>
                  {formData.endDate.error !== "" && (
                    <RequiredMessage>
                      {t(formData.endDate.error)}
                    </RequiredMessage>
                  )}
                </div>
              </GridRow>
              <CheckboxRow>
                <CustomCheckBox
                  id="bulk-generation-not-ready"
                  dataId="bulk-generation-not-ready"
                  labelText={t(
                    "INVOICES_BULK_GENERATION_START_NOT_READY_INVOICING"
                  )}
                  checked={formData.includeNotReady.value}
                  disabled={isValidated}
                  onChange={(e) =>
                    onFormChange("includeNotReady", e.target.checked)
                  }
                />
              </CheckboxRow>
              <SitesSection>
                <SitesHeader>
                  {t("INVOICES_BULK_GENERATION_COUNT")} :{" "}
                  {isValidated ? sites.length : "-"}
                </SitesHeader>
                {isValidated && (
                  <SitesList>
                    {isLoadingSites && (
                      <LoaderContainer>
                        <ClipLoader size={20} color="#1bace4" />
                        {t("BULK_GENERATION_LOADING_RESULTS_MESSAGE")}
                      </LoaderContainer>
                    )}
                    {!isLoadingSites &&
                      sites.map((site) => (
                        <SiteCard key={getSiteId(site)}>
                          <SiteTitle>
                            #{getSiteId(site)} - {getSiteName(site)}
                          </SiteTitle>
                          <SiteClient>{getSiteClient(site)}</SiteClient>
                        </SiteCard>
                      ))}
                  </SitesList>
                )}
              </SitesSection>
            </>
          )}
        </FormBody>
        <ButtonWrapper>
          <Button
            styles={{
              color: "#fff",
              background: "#ccc",
              border: "1px solid #ccc",
              "&:hover": {
                backgroundColor: "#b3b3b3",
              },
            }}
            onClick={onClose}
          >
            {t("CANCEL")}
          </Button>
          {!isValidated && (
            <Button
              styles={{
                color: "white",
                background: "#1BACE4",
                border: "1px solid #1BACE4",
                "&:hover": {
                  backgroundColor: "#1589b6",
                },
              }}
              onClick={onValidate}
            >
              {t("VALIDATE")}
            </Button>
          )}
          {isValidated && !isGenerationStarted && (
            <Button
              styles={{
                color: "white",
                background: "#1BACE4",
                border: "1px solid #1BACE4",
                "&:hover": {
                  backgroundColor: "#1589b6",
                },
              }}
              onClick={onGenerate}
              disabled={isGenerating || isLoadingSites || sites.length === 0}
            >
              <>
                {isGenerating && <ClipLoader size={15} color="#fff" />}
                {!isGenerating && t("GENERATE_INVOICES")}
              </>
            </Button>
          )}
        </ButtonWrapper>
      </div>
    </Dialog>
  );
};

BulkGeneration.propTypes = {
  t: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default withTranslation()(BulkGeneration);
